/**
 * Shared access-token refresh (native fetch).
 * Deduplicates concurrent refresh attempts so refresh-token rotation
 * does not revoke the session when earlyFetch and axios both hit 401.
 */

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const ACCESS_TOKEN_KEY = 'token';
const REFRESH_TOKEN_KEY = 'refresh_token';

let refreshPromise = null;

function persistTokens(accessToken, refreshToken) {
  try {
    if (accessToken) {
      localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    }
    if (refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    }
  } catch (_) {
    /* ignore storage failures */
  }
}

/**
 * Refresh the access token once. Concurrent callers share the same promise.
 * @returns {Promise<string>} new access token
 */
export function refreshAccessTokenOnce() {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    let refreshToken = null;
    try {
      refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    } catch (_) {
      refreshToken = null;
    }
    if (!refreshToken) {
      throw new Error('Missing refresh token');
    }

    const res = await fetch(`${API_URL}/api/auth/refresh`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Skip-Auth-Refresh': 'true',
      },
      credentials: 'omit',
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (!res.ok) {
      const err = new Error(`Token refresh failed: ${res.status}`);
      err.status = res.status;
      throw err;
    }

    const data = await res.json();
    if (!data?.access_token || !data?.refresh_token) {
      throw new Error('Invalid refresh response');
    }

    persistTokens(data.access_token, data.refresh_token);
    return data.access_token;
  })().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
}
